import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { type GroupDefinitionMap } from '~/components/features/GroupCountsFigures'
import { formatNineSegment, type Group, type NineNumber } from '~/utils'

import { lastTabulatedVariablesAtom, tabulationQueryDataAtom } from '../atoms'

// 1グループを構成する (ブランド × 9segsセグメント) セルの区切り。
// グループは選択セルの和集合なので加算記号にしている（日英どちらでもそのまま読める）。
const CELL_SEPARATOR = ' + '

/**
 * 集計済みグループの「定義」（どのブランドのどのセグメントで構成されているか）をグループごとの文字列にして返します。
 *
 * 参照するのは編集中の groupInfos ではなく、
 * いま画面に出ている表を生成した `lastTabulatedVariables` / `tabulationQueryData` です。
 * 集計後にセル選択だけ変えた（まだ再集計していない）状態で、表と食い違う定義を AI に渡さないためです。
 *
 * @returns グループ → 定義文字列。定義を持たないグループは欠落します。
 */
export const useGroupDefinitionMap = (): GroupDefinitionMap => {
  const { t } = useTranslation()
  const lastTabulatedVariables = useAtomValue(lastTabulatedVariablesAtom)
  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)

  return React.useMemo<GroupDefinitionMap>(() => {
    const groupFilters = lastTabulatedVariables?.groupFilters ?? []
    const brands = tabulationQueryData?.brands9segs ?? []

    return R.mapToObj(groupFilters, (groupFilter) => [
      groupFilter.group as Group,
      groupFilter.filters
        .map((filter) => {
          const brandName = brands.find(
            (brand) => brand.number === filter.brand_number,
          )?.name
          const segmentLabel = formatNineSegment(
            filter.segment_number as NineNumber,
            '%t1',
            t,
          )

          // ブランド名が引けないのは集計結果とブランド構成がずれている異常時だけ。
          // 推測でブランド名を作らず、セグメント名だけ出す。
          return brandName === undefined || brandName === ''
            ? segmentLabel
            : `${brandName} ${segmentLabel}`
        })
        .join(CELL_SEPARATOR),
    ])
  }, [lastTabulatedVariables, t, tabulationQueryData])
}
